import React, { useEffect, useRef, useState } from "react";
import styles from './ClinicalCoordinators.module.scss';
import PageTitle from "../../../components/pageTitle/PageTitle";
import DropDownButton from "../../../components/dropDownButton/DropDownButton";
import axiosClient from "../../../api/axiosClient";
import { useToasts } from "../../../contexts/ToastContext";
import LoadMore from "../../../components/loadMore/loadMore";
import StudentAllocationCard from "../../../components/studentAllocationCard/StudentAllocationCard";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import { useNavigate } from "react-router-dom";
import Modal from "../../../components/modal/Modal";
import UploadForm from "../../../components/uploadForm/UploadForm";

const ClinicalCoordinators = () => {
  const limit = 20;
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const [coordinators, setCoordinators] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(100);
  const [refetchTrigger, setRefetchTrigger] = useState(false);
  const { showToast } = useToasts();
  const navigate = useNavigate();
  const progressRef = useRef(0);

  useEffect(() => {
    const fetchData = async () => {
      if (page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadMoreLoading(true);
      }
      await axiosClient.get('users/clinical-coordinator-management/clinical-coordinators', {
        params: { limit, page }
      })
        .then((response) => {
          if (response.data?.clinicalCoordinators) {
            if (page === 1) setCoordinators([]);
            setCoordinators((prev) => [
              ...prev,
              ...response.data.clinicalCoordinators.map((item) => ({
                id: item.id,
                profilePicture: item?.profilePicture ?? null,
                firstName: item?.firstName ?? '',
                lastName: item?.lastName ?? '',
                data: [
                  {
                    label: 'Clinical Site',
                    value: item?.clinicalSites?.length > 0
                      ? `${item.clinicalSites[0].name}${item.clinicalSites.length > 1 ? ` +${item.clinicalSites.length - 1}` : ''}`
                      : '',
                    tooltip: item?.clinicalSites?.length > 1
                      ? item.clinicalSites.map(s => s.name).join(', ')
                      : ''
                  },
                  {
                    label: 'Health System',
                    value: item?.healthSystems?.length > 0
                      ? `${item.healthSystems[0].name}${item.healthSystems.length > 1 ? ` +${item.healthSystems.length - 1}` : ''}`
                      : '',
                    tooltip: item?.healthSystems?.length > 1
                      ? item.healthSystems.map(s => s.name).join(', ')
                      : ''
                  },
                  { label: 'Email', value: item?.email ?? '' },
                  { label: 'Phone', value: item?.phone ?? '' },
                ],
              }))
            ]);
            setTotalPages(response.data.totalPages);
          }
        })
        .catch((error) => showToast(error.message, 'error'))
        .finally(() => {
          setIsLoading(false);
          setIsLoadMoreLoading(false);
        });
    };

    fetchData();
  }, [page, showToast, refetchTrigger]);

  const loadMore = () => setPage(page + 1);

  const showProgress = () => {
    setUploadProgress(0);
    progressRef.current = 0;
    const interval = setInterval(() => {
      progressRef.current += 1;
      setUploadProgress(progressRef.current);
      if (progressRef.current >= 100) clearInterval(interval);
    }, 10);
    return () => clearInterval(interval);
  };

  const handleUpload = async (file) => {
    const formData = new FormData();
    if (file) formData.append('coordinatorInfoFile', file);
    setIsUploadLoading(true);
    showProgress();
    await axiosClient.post('clinical-coordinator/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
      .then((response) => {
        showToast(response.message);
        setShowUploadModal(false);
        setRefetchTrigger(!refetchTrigger);
      })
      .catch((error) => showToast(error.message, 'error'))
      .finally(() => setIsUploadLoading(false));
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle label="Clinical Coordinator Management" />
      </div>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <div style={{ flex: 'none' }}>
            <DropDownButton
              label="Manage Clinical Coordinators"
              items={[
                {
                  label: 'Add New Clinical Coordinator',
                  onClick: () => navigate('/clinical-coordinators/create')
                },
                {
                  label: 'Bulk Upload Clinical Coordinators',
                  onClick: () => setShowUploadModal(true)
                }
              ]}
            />
          </div>
        </div>
        {coordinators.length > 0 ? (
          <div className={styles.cardList}>
            {coordinators.map((item, index) => (
              <div className={styles.cardItem} key={`coordinator-${index}`}>
                <StudentAllocationCard
                  {...item}
                  showActions={true}
                  actions={[
                    {
                      label: 'Edit',
                      onClick: () => navigate(`/clinical-coordinators/edit/${item.id}`)
                    }
                  ]}
                  onClick={() => navigate(`/clinical-coordinators/edit/${item.id}`)}
                />
              </div>
            ))}
            {(isLoadMoreLoading || page < totalPages) && (
              <LoadMore isLoading={isLoadMoreLoading} onClick={loadMore} />
            )}
          </div>
        ) : !isLoading && (
          <div className={styles.emptyList}>
            <p>No clinical coordinators found.</p>
          </div>
        )}
      </div>
      {showUploadModal && (
        <Modal>
          <UploadForm
            isUploading={isUploadLoading}
            uploadProgress={uploadProgress}
            onClose={() => setShowUploadModal(false)}
            onSubmit={handleUpload}
            buttonLabel="Import Clinical Coordinators"
            templateFileUrl="https://maestrostoragedev.blob.core.windows.net/maestro/files/template_for_coordinator.xlsx"
          />
        </Modal>
      )}
    </>
  );
};

export default ClinicalCoordinators;
